import React from 'react';
import { ContextualCTA } from '../CTA';

export default function SuccessStoriesSection() {
  const buyerTestimonials = [
    {
      initials: 'PE',
      title: 'Confidential PE Partner',
      subtitle: 'Lower Middle Market Fund',
      quote:
        'Their data room readiness let us issue an LOI in nine days. Seller diligence was buttoned up before we ever asked for it.',
      avatarBg: 'bg-amiva-primary',
    },
    {
      initials: 'CD',
      title: 'Corporate Development Lead',
      subtitle: 'Strategic Acquirer · Industrial Services',
      quote:
        'We saw real-time engagement metrics on every seller update. It made alignment with our investment committee effortless.',
      avatarBg: 'bg-amiva-accent',
    },
    {
      initials: 'FO',
      title: 'Family Office Principal',
      subtitle: 'Multi-Unit Consumer Services',
      quote: 'They pre-qualified sellers to our criteria before a single meeting. We closed in 74 days with zero renegotiation.',
      avatarBg: 'bg-amber-500',
    },
    {
      initials: 'IS',
      title: 'Independent Sponsor',
      subtitle: 'Healthcare Roll-Up Thesis',
      quote:
        "Weekly buyer rooms and structured updates kept lenders, co-investors, and the seller aligned. Best-run process we've seen.",
      avatarBg: 'bg-emerald-600',
    },
    {
      initials: 'VC',
      title: 'Growth Equity Partner',
      subtitle: 'North America Expansion Fund',
      quote:
        "AMIVA's underwriting memo read like our own IC deck; we moved straight to confirmatory diligence without a retrade.",
      avatarBg: 'bg-indigo-600',
    },
  ];

  return (
    <div className="bg-white">
      {/* Hero Section */}
      <div className="relative bg-gradient-to-br from-gray-900 to-black py-24 lg:py-32 overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-20 pointer-events-none">
          <div className="absolute top-20 left-20 w-96 h-96 bg-amiva-primary rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-20 w-96 h-96 bg-amiva-accent rounded-full blur-3xl"></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="mb-8">
            <span className="bg-white/10 backdrop-blur-sm text-white text-sm font-semibold px-5 py-2.5 rounded-full inline-flex items-center shadow-lg">
              <span className="w-2 h-2 bg-amiva-accent rounded-full mr-2.5 animate-pulse"></span>
              Proven Results
            </span>
          </div>
          <h1 className="text-5xl lg:text-7xl font-bold text-white mb-6 leading-tight">
            Recent <span className="text-gradient">Success Stories</span>
          </h1>
          <p className="text-xl lg:text-2xl text-gray-200 max-w-4xl mx-auto leading-relaxed">
            Real exits. Real founders. Real life-changing outcomes.
          </p>
        </div>
      </div>

      {/* Featured Success Stories */}
      <div className="py-32 lg:py-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Tech SaaS Success */}
            <div className="group">
              <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100 h-full transform transition-all duration-300 hover:-translate-y-2 hover:shadow-2xl">
                <div className="flex items-center justify-between mb-6">
                  <div className="bg-blue-100 rounded-xl p-3">
                    <svg className="w-8 h-8 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M10.394 2.08a1 1 0 00-.788 0l-7 3a1 1 0 000 1.84L5.25 8.051a.999.999 0 01.356-.257l4-1.714a1 1 0 11.788 1.838L7.667 9.088l1.94.831a1 1 0 00.787 0l7-3a1 1 0 000-1.838l-7-3zM3.31 9.397L5 10.12v4.102a8.969 8.969 0 00-1.05-.174 1 1 0 01-.89-.89 11.115 11.115 0 01.25-3.762zM9.3 16.573A9.026 9.026 0 007 14.935v-3.957l1.818.78a3 3 0 002.364 0l5.508-2.361a11.026 11.026 0 01.25 3.762 1 1 0 01-.89.89 8.968 8.968 0 00-5.35 2.524 1 1 0 01-1.4 0zM6 18a1 1 0 001-1v-2.065a8.935 8.935 0 00-2-.712V17a1 1 0 001 1z" />
                    </svg>
                  </div>
                  <span className="text-3xl font-bold text-blue-600">6.2x</span>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">B2B SaaS Platform</h3>
                <div className="space-y-3 mb-6">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Industry:</span>
                    <span className="font-medium text-gray-900">B2B SaaS</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Revenue:</span>
                    <span className="font-medium text-gray-900">$12M ARR</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Buyer:</span>
                    <span className="font-medium text-gray-900">Strategic Acquirer</span>
                  </div>
                </div>
                <div className="pt-6 border-t border-gray-100">
                  <p className="text-sm text-gray-600 italic">
                    "AMIVA got us 3 competing offers. The final price was 55% higher than the first
                    offer."
                  </p>
                  <p className="text-xs text-gray-500 mt-2">- Founder, 2024</p>
                </div>
              </div>
            </div>

            {/* Healthcare Success */}
            <div className="group">
              <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100 h-full transform transition-all duration-300 hover:-translate-y-2 hover:shadow-2xl">
                <div className="flex items-center justify-between mb-6">
                  <div className="bg-emerald-100 rounded-xl p-3">
                    <svg
                      className="w-8 h-8 text-emerald-600"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10 2a1 1 0 00-1 1v1a1 1 0 002 0V3a1 1 0 00-1-1zM4 4h3a3 3 0 006 0h3a2 2 0 012 2v9a2 2 0 01-2 2H4a2 2 0 01-2-2V6a2 2 0 012-2zm2.5 7a1.5 1.5 0 100-3 1.5 1.5 0 000 3zm2.45 4a2.5 2.5 0 10-4.9 0h4.9zM12 9a1 1 0 100 2h3a1 1 0 100-2h-3zm-1 4a1 1 0 011-1h2a1 1 0 110 2h-2a1 1 0 01-1-1z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                  <span className="text-3xl font-bold text-emerald-600">$18M</span>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  Multi-Location Medical Group
                </h3>
                <div className="space-y-3 mb-6">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Specialty:</span>
                    <span className="font-medium text-gray-900">Dental</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Locations:</span>
                    <span className="font-medium text-gray-900">7 Practices</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Buyer:</span>
                    <span className="font-medium text-gray-900">PE-Backed DSO</span>
                  </div>
                </div>
                <div className="pt-6 border-t border-gray-100">
                  <p className="text-sm text-gray-600 italic">
                    "They understood healthcare. Closed in 4 months with minimal disruption to
                    operations."
                  </p>
                  <p className="text-xs text-gray-500 mt-2">- Dr. Owner, 2024</p>
                </div>
              </div>
            </div>

            {/* Service Business Success */}
            <div className="group">
              <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100 h-full transform transition-all duration-300 hover:-translate-y-2 hover:shadow-2xl">
                <div className="flex items-center justify-between mb-6">
                  <div className="bg-amber-100 rounded-xl p-3">
                    <svg className="w-8 h-8 text-amber-600" fill="currentColor" viewBox="0 0 20 20">
                      <path
                        fillRule="evenodd"
                        d="M6 6V5a3 3 0 013-3h2a3 3 0 013 3v1h2a2 2 0 012 2v3.57A22.952 22.952 0 0110 13a22.95 22.95 0 01-8-1.43V8a2 2 0 012-2h2zm2-1a1 1 0 011-1h2a1 1 0 011 1v1H8V5zm1 5a1 1 0 011-1h.01a1 1 0 110 2H10a1 1 0 01-1-1z"
                        clipRule="evenodd"
                      />
                      <path d="M2 13.692V16a2 2 0 002 2h12a2 2 0 002-2v-2.308A24.974 24.974 0 0110 15c-2.796 0-5.487-.46-8-1.308z" />
                    </svg>
                  </div>
                  <span className="text-3xl font-bold text-amber-600">5.9x</span>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Commercial Services Co.</h3>
                <div className="space-y-3 mb-6">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Type:</span>
                    <span className="font-medium text-gray-900">Facility Mgmt</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Footprint:</span>
                    <span className="font-medium text-gray-900">22 Major Facilities</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Structure:</span>
                    <span className="font-medium text-gray-900">20% Rollover</span>
                  </div>
                </div>
                <div className="pt-6 border-t border-gray-100">
                  <p className="text-sm text-gray-600 italic">
                    "Kept equity, stayed as CEO, and doubled the value in 18 months. Perfect
                    outcome."
                  </p>
                  <p className="text-xs text-gray-500 mt-2">- CEO/Founder, 2023</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Detailed Case Studies */}
      <div className="bg-gradient-to-br from-gray-50 to-white py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-20">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
              Deep Dive <span className="text-gradient">Case Studies</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              See exactly how we create competitive bidding environments that maximize value for our
              clients.
            </p>
          </div>

          {/* Case Study 1 */}
          <div className="bg-white rounded-3xl shadow-xl p-12 mb-16">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <div className="inline-flex items-center bg-blue-100 text-blue-700 px-4 py-2 rounded-full text-sm font-semibold mb-6">
                  <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M2 10.5a1.5 1.5 0 113 0v6a1.5 1.5 0 01-3 0v-6zM6 10.333v5.43a2 2 0 001.106 1.79l.05.025A4 4 0 008.943 18h5.416a2 2 0 001.962-1.608l1.2-6A2 2 0 0015.56 8H12V4a2 2 0 00-2-2 1 1 0 00-1 1v.667a4 4 0 01-.8 2.4L6.8 7.933a4 4 0 00-.8 2.4z" />
                  </svg>
                  Technology
                </div>
                <h3 className="text-3xl font-bold text-gray-900 mb-6">
                  How a SaaS Founder Got 6.2x Revenue
                </h3>
                <div className="space-y-4 mb-8">
                  <div className="flex items-start">
                    <div className="w-6 h-6 bg-amiva-primary rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                      <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path
                          fillRule="evenodd"
                          d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </div>
                    <p className="ml-3 text-gray-700">Opened with a lone 4× revenue offer</p>
                  </div>
                  <div className="flex items-start">
                    <div className="w-6 h-6 bg-amiva-primary rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                      <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path
                          fillRule="evenodd"
                          d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </div>
                    <p className="ml-3 text-gray-700">Added three bidders from our network</p>
                  </div>
                  <div className="flex items-start">
                    <div className="w-6 h-6 bg-amiva-primary rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                      <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path
                          fillRule="evenodd"
                          d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </div>
                    <p className="ml-3 text-gray-700">Buyer tension pulled value to 6.2× ARR</p>
                  </div>
                  <div className="flex items-start">
                    <div className="w-6 h-6 bg-amiva-primary rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                      <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path
                          fillRule="evenodd"
                          d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </div>
                    <p className="ml-3 text-gray-700">Locked in a founder-friendly two-year earnout</p>
                  </div>
                </div>
                <div className="bg-blue-50 rounded-2xl p-6">
                  <p className="text-gray-700 italic">
                    "The $8.4M spread between our first and final offers was life-changing wealth we almost left behind."
                  </p>
                  <p className="text-sm text-gray-600 mt-2 font-semibold">- B2B SaaS Founder</p>
                </div>
              </div>
              <div>
                <div className="bg-gradient-to-br from-blue-100 to-blue-50 rounded-2xl p-8">
                  <h4 className="text-xl font-bold text-gray-900 mb-6">Deal Metrics</h4>
                  <div className="space-y-4">
                    <div>
                      <div className="flex justify-between items-baseline mb-2">
                        <span className="text-gray-600">Initial Offer Multiple</span>
                        <span className="text-2xl font-bold text-gray-900">4.0x ARR</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-3">
                        <div className="bg-blue-300 h-3 rounded-full" style={{ width: '65%' }}></div>
                      </div>
                    </div>
                    <div>
                      <div className="flex justify-between items-baseline mb-2">
                        <span className="text-gray-600">Final Sale Multiple</span>
                        <span className="text-2xl font-bold text-blue-600">6.2x ARR</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-3">
                        <div className="bg-blue-600 h-3 rounded-full" style={{ width: '100%' }}></div>
                      </div>
                    </div>
                    <div className="pt-4 mt-4 border-t border-gray-200">
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600">Value Added</span>
                        <span className="text-3xl font-bold text-green-600">+55%</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Case Study 2 */}
          <div className="bg-white rounded-3xl shadow-xl p-12 mb-16">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div className="order-2 lg:order-1">
                <div className="bg-gradient-to-br from-emerald-100 to-emerald-50 rounded-2xl p-8">
                  <h4 className="text-xl font-bold text-gray-900 mb-6">Transaction Highlights</h4>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between p-4 bg-white rounded-xl">
                      <span className="text-gray-600">Locations</span>
                      <span className="font-bold text-gray-900">7 Practices</span>
                    </div>
                    <div className="flex items-center justify-between p-4 bg-white rounded-xl">
                      <span className="text-gray-600">EBITDA Multiple</span>
                      <span className="font-bold text-gray-900">8.5x</span>
                    </div>
                    <div className="flex items-center justify-between p-4 bg-white rounded-xl">
                      <span className="text-gray-600">Time to Close</span>
                      <span className="font-bold text-gray-900">4 Months</span>
                    </div>
                    <div className="flex items-center justify-between p-4 bg-white rounded-xl">
                      <span className="text-gray-600">Post-Close Role</span>
                      <span className="font-bold text-gray-900">Advisory</span>
                    </div>
                  </div>
                </div>
              </div>
              <div className="order-1 lg:order-2">
                <div className="inline-flex items-center bg-emerald-100 text-emerald-700 px-4 py-2 rounded-full text-sm font-semibold mb-6">
                  <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M10 2a1 1 0 00-1 1v1a1 1 0 002 0V3a1 1 0 00-1-1zM4 4h3a3 3 0 006 0h3a2 2 0 012 2v9a2 2 0 01-2 2H4a2 2 0 01-2-2V6a2 2 0 012-2zm2.5 7a1.5 1.5 0 100-3 1.5 1.5 0 000 3zm2.45 4a2.5 2.5 0 10-4.9 0h4.9zM12 9a1 1 0 100 2h3a1 1 0 100-2h-3zm-1 4a1 1 0 011-1h2a1 1 0 110 2h-2a1 1 0 01-1-1z"
                      clipRule="evenodd"
                    />
                  </svg>
                  Healthcare
                </div>
                <h3 className="text-3xl font-bold text-gray-900 mb-6">
                  Dental Group's Strategic Exit to PE
                </h3>
                <div className="space-y-4 mb-8">
                  <div className="flex items-start">
                    <div className="w-6 h-6 bg-amiva-accent rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                      <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path
                          fillRule="evenodd"
                          d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </div>
                    <p className="ml-3 text-gray-700">Targeted PE-backed DSOs already buying locally</p>
                  </div>
                  <div className="flex items-start">
                    <div className="w-6 h-6 bg-amiva-accent rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                      <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path
                          fillRule="evenodd"
                          d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </div>
                    <p className="ml-3 text-gray-700">Spotlighted their tech-forward, systemized ops</p>
                  </div>
                  <div className="flex items-start">
                    <div className="w-6 h-6 bg-amiva-accent rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                      <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path
                          fillRule="evenodd"
                          d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </div>
                    <p className="ml-3 text-gray-700">Held the post-close commitment to six months</p>
                  </div>
                  <div className="flex items-start">
                    <div className="w-6 h-6 bg-amiva-accent rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                      <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path
                          fillRule="evenodd"
                          d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </div>
                    <p className="ml-3 text-gray-700">Closed in four months with zero disruption</p>
                  </div>
                </div>
                <div className="bg-emerald-50 rounded-2xl p-6">
                  <p className="text-gray-700 italic">
                    "AMIVA knew exactly which buyers valued our systems and patient retention."
                  </p>
                  <p className="text-sm text-gray-600 mt-2 font-semibold">
                    - Practice Owner
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Case Study 3 */}
          <div className="bg-white rounded-3xl shadow-xl p-12">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <div className="inline-flex items-center bg-amber-100 text-amber-700 px-4 py-2 rounded-full text-sm font-semibold mb-6">
                  <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M10 2a1 1 0 00-1 1v1a1 1 0 002 0V3a1 1 0 00-1-1zM4 4h12a2 2 0 012 2v9a2 2 0 01-2 2H4a2 2 0 01-2-2V6a2 2 0 012-2zm4.5 8a1.5 1.5 0 10-3 0 1.5 1.5 0 003 0zm7.5-4H11a1 1 0 110-2h5a1 1 0 110 2zm0 4h-5a1 1 0 100 2h5a1 1 0 100-2z"
                      clipRule="evenodd"
                    />
                  </svg>
                  Facility Services
                </div>
                <h3 className="text-3xl font-bold text-gray-900 mb-6">
                  Commercial Cleaning Roll-Up Achieves Premium Exit
                </h3>
                <div className="space-y-4 mb-8">
                  <div className="flex items-start">
                    <div className="w-6 h-6 bg-amber-500 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                      <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path
                          fillRule="evenodd"
                          d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </div>
                    <p className="ml-3 text-gray-700">
                      Converted project work into 3-year contracts before going to market
                    </p>
                  </div>
                  <div className="flex items-start">
                    <div className="w-6 h-6 bg-amber-500 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                      <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path
                          fillRule="evenodd"
                          d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 01-1.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </div>
                    <p className="ml-3 text-gray-700">
                      Pitted national platforms against PE-backed regional groups
                    </p>
                  </div>
                  <div className="flex items-start">
                    <div className="w-6 h-6 bg-amber-500 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                      <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path
                          fillRule="evenodd"
                          d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </div>
                    <p className="ml-3 text-gray-700">
                      Showed 99% SLA compliance through KPI dashboards
                    </p>
                  </div>
                  <div className="flex items-start">
                    <div className="w-6 h-6 bg-amber-500 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                      <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path
                          fillRule="evenodd"
                          d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </div>
                    <p className="ml-3 text-gray-700">
                      Tied rollover upside directly to contract retention
                    </p>
                  </div>
                </div>
                <div className="bg-amber-50 rounded-2xl p-6">
                  <p className="text-gray-700 italic">
                    "We assumed buyers would discount janitorial contracts. AMIVA reframed our renewals and beat the board's target by 85%."
                  </p>
                  <p className="text-sm text-gray-600 mt-2 font-semibold">
                    - CEO, Commercial Cleaning Company
                  </p>
                </div>
              </div>
              <div>
                <div className="bg-gradient-to-br from-amber-100 to-amber-50 rounded-2xl p-8">
                  <h4 className="text-xl font-bold text-gray-900 mb-6">Deal Metrics</h4>
                  <div className="space-y-4">
                    <div>
                      <div className="flex justify-between items-baseline mb-2">
                        <span className="text-gray-600">Initial Offer Multiple</span>
                        <span className="text-2xl font-bold text-gray-900">3.2x EBITDA</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-3">
                        <div className="bg-amber-300 h-3 rounded-full" style={{ width: '54%' }}></div>
                      </div>
                    </div>
                    <div>
                      <div className="flex justify-between items-baseline mb-2">
                        <span className="text-gray-600">Final Sale Multiple</span>
                        <span className="text-2xl font-bold text-amber-600">5.9x EBITDA</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-3">
                        <div className="bg-amber-500 h-3 rounded-full" style={{ width: '100%' }}></div>
                      </div>
                    </div>
                    <div className="pt-4 mt-4 border-t border-gray-200">
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600">Value Added</span>
                        <span className="text-3xl font-bold text-green-600">+85%</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Testimonials Grid */}
      <div className="bg-white py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-20">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
              What Our Clients <span className="text-gradient">Say</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Hear directly from the buy-side partners who trust AMIVA to deliver qualified
              acquisition opportunities. Identifiers are anonymized to respect confidential mandates.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {buyerTestimonials.map((testimonial, index) => (
              <div
                key={`${testimonial.initials}-${index}`}
                className="bg-gradient-to-br from-gray-50 to-white rounded-2xl p-8 shadow-lg border border-gray-100"
              >
                <div className="flex items-center mb-6">
                  <div
                    className={`w-12 h-12 rounded-full flex items-center justify-center text-white font-bold text-xl ${testimonial.avatarBg}`}
                  >
                    {testimonial.initials}
                  </div>
                  <div className="ml-4">
                    <h4 className="font-bold text-gray-900">{testimonial.title}</h4>
                    <p className="text-sm text-gray-600">{testimonial.subtitle}</p>
                  </div>
                </div>
                <p className="text-gray-700 italic leading-relaxed">
                  "{testimonial.quote}"
                </p>
                <div className="mt-6 flex items-center text-amber-500">
                  {[...Array(5)].map((_, starIndex) => (
                    <svg key={starIndex} className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* CTA Section - Contextual variant for success stories page */}
      <ContextualCTA variant="stories" />
    </div>
  );
}
